import { combineReducers } from "redux";
import _ from "lodash";

import local from "./local";
import employee from "./employee";
import store from "./store";
import campaign from "./campaign";
import item, { getMarketModifiers } from "./item";
import customer from "./customer";
import report from "./report";
import order from "./order";
import coupon from "./coupon";
import market, * as fromMarket from "./market";
import editing from "./editing";
import subscription from "./subscription";
import settlements from "./settlements";
import terminals from "./terminals";
import inventory from "./inventory";
import equipment from "./equipment";
import productCode from "./productCode";
import purchaseOrder from "./purchaseOrder";
import delivery from "./delivery";
import shipment from "./shipment";
import deliverySettings from "./deliverySettings";
import cs from "./cs";
import supplier from "./supplier";
import mgmt from "./mgmt";
import waiting from "./waiting";
import providerConfig from "./providerConfig";
import isms, { getIsms } from "./isms";
import Authority from "./../services/Authority";
import Constants, { EXCEPTION_RETAIL_STOREIDS } from "../helpers/Constants";
import moment from "moment";

export default combineReducers({
  local,
  employee,
  store,
  item,
  campaign,
  customer,
  report,
  order,
  market,
  coupon,
  editing,
  subscription,
  settlements,
  terminals,
  inventory,
  equipment,
  productCode,
  purchaseOrder,
  delivery,
  shipment,
  deliverySettings,
  cs,
  supplier,
  mgmt,
  waiting,
  providerConfig,
  isms,
});

export { getIsms };

export function getSignUpState(state) {
  return state.local.signUp;
}

export function isNarrowWidth(state) {
  return state.local.narrowWidth.narrowWidth;
}

export function getPdfStatus(state) {
  return state.local.pdfStatus;
}

export function getDropoutStatus(state) {
  return state.local.dropoutStatus;
}

export function getOngoingRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) => e.status === "ongoing" && e.progress !== 100,
  );
}

export function getOngoingStockRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) =>
      e.status === "ongoing" &&
      e.progress !== 100 &&
      [
        Constants.ASYNC_PROGRESS_FUNC.CANCEL_PURCHASE_ORDER,
        Constants.ASYNC_PROGRESS_FUNC.APPLY_PURCHASE_ORDER,
        Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_TRANSFER_HISTORY,
        Constants.ASYNC_PROGRESS_FUNC.CANCEL_INVENTORY_TRANSFER_HISTORY,
        Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_COUNT_HISTORY,
        Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_ADJUSTMENTS,
        Constants.ASYNC_PROGRESS_FUNC.SYNC_INVENTORIES,
      ].includes(e.func),
  );
}

export function getOngoingPurchaseOrderRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) =>
      e.status === "ongoing" &&
      e.progress !== 100 &&
      [
        Constants.ASYNC_PROGRESS_FUNC.CANCEL_PURCHASE_ORDER,
        Constants.ASYNC_PROGRESS_FUNC.APPLY_PURCHASE_ORDER,
      ].includes(e.func),
  );
}

export function getOngoingTransferRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) =>
      e.status === "ongoing" &&
      e.progress !== 100 &&
      [
        Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_TRANSFER_HISTORY,
        Constants.ASYNC_PROGRESS_FUNC.CANCEL_INVENTORY_TRANSFER_HISTORY,
      ].includes(e.func),
  );
}

export function getOngoingInventoryRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) =>
      e.status === "ongoing" &&
      e.progress !== 100 &&
      [Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_REQUEST_HISTORY].includes(
        e.func,
      ),
  );
}

export function getOngoingProductsProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) =>
      e.status === "ongoing" &&
      e.progress !== 100 &&
      [Constants.ASYNC_PROGRESS_FUNC.SYNC_PRODUCTS].includes(e.func),
  );
}

export function getFailedRequestProgressEvents(state) {
  return Object.values(state.local.requestProgressEvents).filter(
    (e) => e.status === "done" && e.errorMessage,
  );
}

export function getAuthorizeModal(state) {
  return state.local.authorizeModal;
}

export function getModal(state) {
  return state.local.modal;
}

export function getLocationSelectModal(state) {
  return state.local.locationSelectModal;
}

export function getEnvironment(state) {
  return state.local.environment;
}

export function getLocalEnvironment(state) {
  return state.local.localEnvironment;
}

export function getCustomerOpenState(state) {
  return state.local.customerInfo;
}

export function getEditingLocation(state) {
  return state.editing.location;
}

export function getEditingPlan(state) {
  return state.editing.plan;
}

export function getEditingEmployee(state) {
  return state.editing.employee;
}

export function storeStatus(state) {
  return state.store.storeStatus;
}

export function getCurrentCost(state) {
  return state.store.cost;
}

export function getCostStatus(state) {
  return state.store.costStatus;
}

export function billingHistoriesStatus(state) {
  return state.store.billingHistoriesStatus;
}

export function isStoreFetching(state) {
  return state.store.storeStatus.isFetching;
}

export function isBillingHistoriesFetching(state) {
  return state.store.billingHistoriesStatus.isFetching;
}

export function getStore(state) {
  return state.store.store || {};
}

export function getStoreStatus(state) {
  return state.store.storeStatus;
}

export function getStoreError(state) {
  return state.store.storeStatus.error;
}

export function getStoreCustomerGroups(state) {
  return state.store.store.groups ? state.store.store.groups.items : [];
}

export function getStoreCustomerConfig(state) {
  return state.store.store.customer || {};
}

export function getBillingHistoriesError(state) {
  return state.store.billingHistoriesStatus.error;
}

export function getStoreInfo(state) {
  return (state.store.store.info ? state.store.store.info : {}) || {};
}

export function getStoreItemConfig(state) {
  return (state.store.store.item ? state.store.store.item : {}) || {};
}

export function getStoreLoyalty(state) {
  return getStore(state).loyalty || {};
}

export function getStoreLoyaltyRwardMinRequireRate(state) {
  return getStoreLoyalty(state).reward?.minRequireRate || 0;
}

export function getStoreInventoryConfig(state) {
  return (state.store.store.inventory ? state.store.store.inventory : {}) || {};
}

export function isHqWhExposureEnabled(state) {
  const inventoryConfig = getStoreInventoryConfig(state);
  return inventoryConfig?.hqWhExposureEnabled !== false;
}

export function isHeadquarterInventoryVisibleAtLocation(
  state,
  locationId = null,
) {
  if (!isHqWhExposureEnabled(state)) {
    return false;
  }

  const inventoryConfig = getStoreInventoryConfig(state);
  const exposureLocIds = inventoryConfig?.hqWhExposureLocIds || [];
  const unexposureLocIds = inventoryConfig?.hqWhUnexposureLocIds || [];
  const targetLocationId =
    locationId || getCurrentOrganization(state)?.locationId || null;

  if (!targetLocationId) {
    return true;
  }
  if (exposureLocIds.length > 0) {
    return exposureLocIds.includes(targetLocationId);
  }
  if (unexposureLocIds.length > 0) {
    return !unexposureLocIds.includes(targetLocationId);
  }
  return true;
}

export function getStoreMarketConfig(state) {
  return (state.store.store.market ? state.store.store.market : {}) || {};
}

export function getStoreLocations(state) {
  return (state.store.store.locations?.items || []).filter(
    (i) => i.type !== "headquarter",
  );
}

export function getAllLocations(state) {
  return state.store.store.locations?.items || [];
}

export function getAllLocationsById(state) {
  const all = getAllLocations(state);
  return all.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInventoryHeadquarter(state) {
  const store = getStore(state);
  const retailMode = store.settings?.mode === "retail";
  const hasMulti = isMultiLocation(state);
  const hasConctract = isContracted(state);
  const headquarter =
    (state.store.store.locations?.items || []).find(
      (i) => i.type === "headquarter",
    ) || {};
  return hasConctract && ((!retailMode && hasMulti) || retailMode)
    ? headquarter
    : {};
}

export function getStoreLocationsById(state) {
  return getStoreLocations(state).reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getOfflineLocations(state) {
  let locations = getStoreLocations(state);
  return locations.filter((loc) => !loc.provider && loc.type !== "warehouse");
}

export function getActiveLocations(state) {
  let locations = getStoreLocations(state);
  return locations.filter((loc) => loc.state !== "inactive");
}

export function getActiveOfflineLocations(state) {
  let locations = getOfflineLocations(state);
  return locations.filter((loc) => loc.state !== "inactive");
}

export function getWarehouseLocations(state) {
  let locations = getStoreLocations(state);
  const hasAdmin = hasLocationAdminAccess(state);
  const headquarter = getInventoryHeadquarter(state);
  return [
    ...(hasAdmin ? (!_.isEmpty(headquarter) ? [headquarter] : []) : []),
    ...locations.filter((loc) => loc.type === "warehouse"),
  ];
}

export function getWarehouseLocationsForView(state, locationId = null) {
  let locations = getStoreLocations(state);
  const headquarter = getInventoryHeadquarter(state);
  const isHeadquarterVisible = isHeadquarterInventoryVisibleAtLocation(
    state,
    locationId,
  );
  return [
    ...(isHeadquarterVisible && !_.isEmpty(headquarter) ? [headquarter] : []),
    ...locations.filter((loc) => loc.type === "warehouse"),
  ];
}

export function getInactiveLocations(state) {
  let locations = getStoreLocations(state);
  return locations.filter((loc) => loc.state === "inactive");
}

export function getExternalLocations(state) {
  let locations = getStoreLocations(state);
  return locations.filter((loc) => loc.provider && loc.type === "external");
}

export function getCafe24Location(state) {
  const locations = getExternalLocations(state);
  return locations.filter((loc) => loc.provider === "cafe24")[0] || {};
}

export function getOfflineLocationsById(state) {
  let locations = getOfflineLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getActvieOfflineLocationsById(state) {
  let locations = getActiveOfflineLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getWarehouseLocationsById(state) {
  let locations = getWarehouseLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getWarehouseLocationsForViewById(state, locationId = null) {
  let locations = getWarehouseLocationsForView(state, locationId);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInventoryLocations(state) {
  const warehouseLocations = getWarehouseLocations(state);
  const offlineLocations = getActiveOfflineLocations(state);
  return [...warehouseLocations, ...offlineLocations];
}

export function getInventoryLocationsForView(state, locationId = null) {
  const warehouseLocations = getWarehouseLocationsForView(state, locationId);
  const offlineLocations = getActiveOfflineLocations(state);
  return [...warehouseLocations, ...offlineLocations];
}

export function getInventoryLocationsIncludingInactive(state) {
  const warehouseLocations = getWarehouseLocations(state);
  const offlineLocations = getOfflineLocations(state);
  const cafe24Location = getCafe24Location(state);
  const cafe24Provider = getCafe24Provider(state);
  if (!_.isEmpty(cafe24Provider) && cafe24Provider?.inventory) {
    return [...warehouseLocations, ...offlineLocations, cafe24Location];
  } else {
    return [...warehouseLocations, ...offlineLocations];
  }
}

export function getInventoryLocationsForViewIncludingInactive(
  state,
  locationId = null,
) {
  const warehouseLocations = getWarehouseLocationsForView(state, locationId);
  const offlineLocations = getOfflineLocations(state);
  const cafe24Location = getCafe24Location(state);
  const cafe24Provider = getCafe24Provider(state);
  if (!_.isEmpty(cafe24Provider) && cafe24Provider?.inventory) {
    return [...warehouseLocations, ...offlineLocations, cafe24Location];
  } else {
    return [...warehouseLocations, ...offlineLocations];
  }
}

export function getInventoryLocationsById(state) {
  const locations = getInventoryLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInventoryLocationsForViewById(state, locationId = null) {
  const locations = getInventoryLocationsForView(state, locationId);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInventoryLocationsIncludingInactiveById(state) {
  const locations = getInventoryLocationsIncludingInactive(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInventoryLocationsForViewIncludingInactiveById(
  state,
  locationId = null,
) {
  const locations = getInventoryLocationsForViewIncludingInactive(
    state,
    locationId,
  );
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function isAbleToTransfer(state) {
  const inventoryLocations = getInventoryLocations(state);
  return (inventoryLocations || []).length > 1;
}

export function getActiveStoreLocationsById(state) {
  let locations = getActiveLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getInactiveLocationsById(state) {
  let locations = getInactiveLocations(state);
  return locations.reduce((obj, loc) => {
    obj[loc.id] = _.cloneDeep(loc);
    return obj;
  }, {});
}

export function getLocationsWithLocationAdminAccess(state) {
  let locations = getStoreLocations(state);
  const currentOrg = getCurrentOrganization(state);
  const hasAdminAccess = hasLocationAdminAccess(state);
  return (locations || []).filter(
    (loc) =>
      loc.id === currentOrg.locationId ||
      loc.type === "external" ||
      hasAdminAccess,
  );
}

export function getStoreNotification(state) {
  return state.store.store.notification ? state.store.store.notification : {};
}

export function getReceiptConfig(state) {
  const _isMultiLocation = isMultiLocation(state);
  if (_isMultiLocation) {
    return getCurrentLocation(state).receipt || {};
  } else {
    return state.store.store.receipt || {};
  }
}

export function getViewConfig(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  return _isMultiLocation && Authority.level() !== "store"
    ? location.view
    : store.view;
}

export function isInactiveLcoation(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  return (
    _isMultiLocation &&
    location.state === "inactive" &&
    Authority.level() !== "store"
  );
}

export function getStorePayments(state) {
  return state.store.store.payments ? state.store.store.payments : {};
}

export function getPaymentsForTaxRefund(state) {
  const _isMultiLocation = isMultiLocation(state);
  if (_isMultiLocation) {
    return getCurrentLocation(state).payments || {};
  } else {
    return state.store.store.payments || {};
  }
}

export function getStoreContract(state) {
  return state.store.contract.store || {};
}

/**
 * 환급(사후면세) 사용 매장인가 — 계약의 `taxRefund` 가 유일한 근거다.
 * 🔴 업종(isRetailMode)으로 가르지 않는다 [우진 QA 2026-08-14 — "외식업에 환급
 *    탭이 나온다"]. `settings.mode` 는 비어 있는 계정이 있어(소매인데 null)
 *    업종으로 가르면 **정상 소매 매장에서 환급 탭이 사라진다**. 계약 플래그는
 *    환급을 실제로 쓰는지를 직접 담고 있어 그 오탐이 없다.
 * 지점 계약(location)이 따로 있으면 그쪽도 인정한다.
 */
export function usesTaxRefund(state) {
  const storeContract = state.store.contract.store || {};
  const locationContract = state.store.contract.location || {};
  return !!(storeContract.taxRefund || locationContract.taxRefund);
}

export function getStoreSubscriptions(state) {
  const storeContract = getStoreContract(state);
  return storeContract?.subscriptions || [];
}

export function getPosSubscriptionPlan(state) {
  const storeSubscriptions = getStoreSubscriptions(state);
  const posSubscription = (storeSubscriptions.filter(
    (plan) => plan?.type === "pos",
  ) || [])[0];
  return posSubscription || {};
}

export function getMembershipSubscriptionPlan(state) {
  const storeSubscriptions = getStoreSubscriptions(state);
  const membershipPlans = (storeSubscriptions.filter(
    (plan) => plan?.type === "cafe24" || plan?.type === "imweb",
  ) || [])[0];
  return membershipPlans || {};
}

export function isValidMembershipSubscriptionPlan(state) {
  const membershipPlan = getMembershipSubscriptionPlan(state);
  return (
    membershipPlan?.planId !== "csp_membership_free" &&
    moment(membershipPlan?.expiryDate).isSameOrAfter(moment(), "day")
  );
}

export function isOnlineProviderKey(state) {
  const storeProvider = getStorePolicyProvider(state);
  return (
    !_.isEmpty(storeProvider) && ["cafe24", "imweb"].includes(storeProvider.key)
  );
}

export function isContracted(state) {
  const storeContract = state.store.contract.store || {};
  const posSubscription = getPosSubscriptionPlan(state);
  const membershipSubscription = getMembershipSubscriptionPlan(state);
  return (
    storeContract?.businessOperatorId ||
    ((storeContract?.cafe24 || storeContract?.imweb) &&
      (membershipSubscription.status === "active" ||
        membershipSubscription.status === "canceled")) ||
    posSubscription?.status === "active" ||
    posSubscription?.status === "canceled"
  );
}

export function retrieveProviderCustomerCount(state) {
  return state.providerConfig.providerCustomerCount;
}

export function isSubscribed(state) {
  const storeContract = state.store.contract.store || {};
  return storeContract?.subscribed;
}

export function getStoreBilling(state) {
  return state.store.store.billing ? state.store.store.billing : {};
}

export function getBilling(state) {
  const location = getCurrentLocation(state);
  const org = getCurrentOrganization(state);
  if (org.role && !["owner", "admin"].includes(org.role)) {
    if (location.type === "indirect") {
      return location.billing || {};
    }
  }
  return state.store.store?.billing || {};
}

export function getRequestProgressEventOrigin(state) {
  const org = getCurrentOrganization(state);
  const originId = ["owner", "admin"].includes(org.role)
    ? org.storeId
    : org.locationId;
  return { name: "dashboard", id: originId };
}

export function getStoreDevices(state) {
  return state.store.store.devices ? state.store.store.devices : [];
}

export function getPosMiniDevices(state) {
  let storeDevices = getStoreDevices(state);
  return storeDevices.filter(
    (d) => d.type.toLowerCase() === "pos" || d.type.toLowerCase() === "mini",
  );
}

export function getStoreBillingHistories(state) {
  return state.store.billingHistories ? state.store.billingHistories : [];
}

export function getMembershipBillingHistories(state) {
  const histories = getStoreBillingHistories(state);
  return (
    histories.filter((h) => h.type === "cafe24" || h.type === "imweb") || []
  );
}

export function getGeneralBillingHistories(state) {
  const histories = getStoreBillingHistories(state);
  return (
    histories.filter((h) => h.type !== "cafe24" && h.type !== "imweb") || []
  );
}

export function getStoreFailedBillingHistories(state) {
  return getStoreBillingHistories(state).filter(
    (his) => his.status === "failed",
  );
}

export function isBillingFailed(state) {
  return (getStoreFailedBillingHistories(state) || []).length > 0;
}

export function getStoreRegularBillingHistories(state) {
  return getStoreBillingHistories(state).filter(
    (his) => his.type === "regular",
  );
}

export function getFailedRegualarBillingHistories(state) {
  const regularBillingHistories = getStoreRegularBillingHistories(state);
  return regularBillingHistories.filter((his) => his.status === "failed");
}

export function getStoreId(state) {
  return state.store.store ? state.store.store.id : null;
}

export function getStoreName(state) {
  return state.store.store.info ? state.store.store.info.name : null;
}

export function getStoreTaxes(state) {
  return state.store.store.taxes ? state.store.store.taxes : [];
}

export function getLocationPhone(state) {
  const multiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  return multiLocation && Authority.level() !== "store"
    ? location.phone
    : store.info.phone;
}

export function getLocationName(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  return _isMultiLocation && Authority.level() !== "store"
    ? location.name
    : store.info?.name;
}

export function getSettings(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  return _isMultiLocation && Authority.level() !== "store"
    ? location.settings
    : store.settings;
}

export function isRetailMode(state) {
  const store = getStore(state);
  return (
    store.settings?.mode === "retail" ||
    EXCEPTION_RETAIL_STOREIDS.includes(store.id)
  );
}

export function getStoreSettings(state) {
  const store = getStore(state);
  return store.settings;
}

export function getReportBasis(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  return _isMultiLocation && Authority.level() !== "store"
    ? (location.settings?.reportBasis ?? null)
    : (store.settings?.reportBasis ?? null);
}

export function getSalesStartedAt(state) {
  const location = getCurrentLocation(state);
  const _isMultiLocation = isMultiLocation(state);
  const _startedAt = location.sales?.startedAt ?? null;
  const isHeadOffice = _isMultiLocation && Authority.level() === "store";
  return isHeadOffice ? new Date() : _startedAt;
}

export function getSalesStartedAtForSalesDate(state) {
  const location = getCurrentLocation(state);
  const _startedAt = location.state === "opened" && location.sales?.startedAt;
  return _startedAt ? _startedAt : null;
}

export function getSalesStartedAtForMarket(state) {
  const storeInfo = getCartStoreInfo(state);
  const _startedAt =
    storeInfo?.state === "opened" && storeInfo.sales?.startedAt;
  return _startedAt ? _startedAt : null;
}

export function getLoyalty(state) {
  return state.store.store.loyalty ? state.store.store.loyalty : {};
}

export function getMarketConfig(state) {
  return state.store.store.market ? state.store.store.market : {};
}

export function getKioskConfig(state) {
  return state.store.store.kiosk ? state.store.store.kiosk : {};
}

export function getKakaoConfig(state) {
  return state.store.store.kakao || {};
}

export function isKakaoChannelActive(state) {
  const kakao = state.store.store.kakao;
  return kakao?.channelId && kakao?.status === "active";
}

export function getStorePolicy(state) {
  return state.store.store.policy || {};
}

export function getMenuPlans(state) {
  return state.store.store.plans?.menus || [];
}

export function isSyncMenuStatusFetching(state) {
  return state.store.menuStatus.isFetching;
}

export function getMenuSyncStatusError(state) {
  return state.store.menuStatus.error;
}

export function getOrderPlans(state) {
  return state.store.store.plans?.orders || [];
}

export function getSubscriptionConfig(state) {
  return state.store.store.subscription ? state.store.store.subscription : {};
}

export function getMarketAccount(state) {
  const multiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  if (multiLocation && location.type === "indirect") {
    return location.market?.account || {};
  } else {
    return state.store.store.market?.account || {};
  }
}

export function getSubscriptionAccount(state) {
  const multiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  if (multiLocation && location.type === "indirect") {
    return location.subscription?.account || {};
  } else {
    return state.store.store.subscription?.account || {};
  }
}

export function getLabelTemplates(state) {
  return state.store.templates || [];
}

export function getTemplateStatus(state) {
  return state.store.templateStatus || [];
}

export function getCampaignsById(state) {
  return state.campaign.campaigns.byId || {};
}

export function getCampaignsCount(state) {
  return state.campaign.campaigns.count || 0;
}

export function getCampaigns(state) {
  return Object.values(state.campaign.campaigns.byId || {});
}

export function getMessageCampaignReport(state) {
  return state.campaign.messageCampaignReport;
}
export function getCampaignStatus(state) {
  return state.campaign.campaignStatus;
}

export function getExpectedRecipientNumber(state) {
  return state.campaign.expectedRecipientNumber;
}

// fallback 은 모듈 상수로 고정한다 — 셀렉터가 매번 새 {} 를 만들면 useSelector 가
// 항상 참조 불일치로 보고 리렌더한다.
const EMPTY_BRAND_MESSAGE_STATUS = {};

// 카카오 발신프로필(다우 라이브). 조회 전이면 null.
// 다우 응답에 없는 키는 생략되므로 `profile?.x === true` / `!!profile?.x` 로 판정할 것.
// 액션 크리에이터 actions.getKakaoProfile 과 동명이다 — 페이지에서 별칭 import 할 것
// (선례: ChannelSettings.js 의 getAvailableShops as fetchAvailableShops).
export function getKakaoProfile(state) {
  return state.campaign.kakaoProfile;
}

export function getBrandMessageStatus(state) {
  return state.campaign.brandMessageStatus || EMPTY_BRAND_MESSAGE_STATUS;
}

// 신청 응답 원문 { unsubscribe?, evidence?, apply }. 실패/재신청 시 null 로 리셋된다.
export function getBrandMessageApply(state) {
  return state.campaign.brandMessageApply;
}

// 마케팅 대상(M/N/O) 브랜드메시지 승인 여부. 서버 campaign.py:194 와 동일 판정.
export function isBrandMessageApproved(state) {
  return state.campaign.kakaoProfile?.brandMessage === true;
}

// 080 무료수신거부 번호. 없으면 M/N/O 발송 불가 (server campaign.py:197).
export function getUnsubscribePhoneNumber(state) {
  return state.campaign.kakaoProfile?.unsubscribePhoneNumber || null;
}

// friend(채널 친구) 타깃은 승인 불필요. 마케팅 대상만 승인 + 080 둘 다 필요하다.
export function canSendBrandMessageMarketing(state) {
  return isBrandMessageApproved(state) && !!getUnsubscribePhoneNumber(state);
}

// 브랜드메시지 진입 가능 여부 = 채널 연동(로컬) + 프로필 활성(라이브).
// profileStatus: A=활성 C=비활성 B=차단 E=삭제중 D=삭제 (server daou.py:312)
export function canUseBrandMessage(state) {
  const profile = state.campaign.kakaoProfile;
  return (
    !!isKakaoChannelActive(state) &&
    profile?.profileStatus === "A" &&
    profile?.block !== true &&
    profile?.dormant !== true
  );
}

export function getItemStatus(state) {
  return state.item.itemStatus;
}

/** 다건 복사 결과 { successCount, failCount } — 완료 팝업(figma 1491:41750)용. 없으면 null. */
export function getCopyProductsResult(state) {
  return state.item.copyResult;
}

export function isLegacyItems(state) {
  return state.item.items.legacy;
}

export function getItems(state) {
  return state.item.items;
}

export function getProductsWithDependency(state) {
  const currentLocationId = getCurrentLocationId(state);
  const items = getItems(state);
  const categoriesByProduct = items.categoryProducts.reduce((obj, pc) => {
    if (!(pc.productId in obj)) {
      obj[pc.productId] = [];
    }
    obj[pc.productId] = [...obj[pc.productId], pc.categoryId];
    return obj;
  }, {});
  const modifiersByProduct = items.productModifiers.reduce((obj, pc) => {
    if (!(pc.productId in obj)) {
      obj[pc.productId] = [];
    }
    obj[pc.productId] = [...obj[pc.productId], pc.modifierId];
    return obj;
  }, {});

  return items.products.map((p) => ({
    ...p,
    locationIds:
      !p.locationIds || p.locationIds.length === 0
        ? [currentLocationId]
        : p.locationIds,
    modifierIds: modifiersByProduct[p.id] || [],
    categoryIds: categoriesByProduct[p.id] || [],
  }));
}

export function getItemInventoryHistories(state) {
  return state.item.histories;
}

export function getInventoryHistories(state) {
  return state.inventory.histories;
}

export function getInventoryHistoriesById(state) {
  return state.inventory.histories.byId;
}

export function getInventoryHistorySummaryBySkuId(state) {
  return state.inventory.summary.bySkuId;
}

export function getInventoryHistoryCount(state) {
  return state.inventory.histories.count;
}

export function getInventoryHistoryItemCount(state) {
  return state.inventory.histories.currentItemCount;
}

export function getInventoryTransferHistoriesById(state) {
  return state.inventory.transfer.byId;
}

export function getInventoryRequestsById(state) {
  return state.inventory.newRequests.byId;
}

export function getInventoryNewRequestsById(state) {
  let hisById = getInventoryRequestsById(state);
  return Object.values(hisById || {})
    .filter((h) => h.type === "new")
    .reduce((obj, his) => {
      obj[his.id] = his;
      return obj;
    }, {});
}

export function getInventoryReturnRequestsById(state) {
  let hisById = getInventoryRequestsById(state);
  return Object.values(hisById || {})
    .filter((h) => h.type === "returns")
    .reduce((obj, his) => {
      obj[his.id] = his;
      return obj;
    }, {});
}

export function getInventoryTransferHistoryCount(state) {
  return state.inventory.transfer.count;
}

export function receiveInventoryCountHistoriesById(state) {
  return state.inventory.count.byId;
}

export function receiveInventoryCountHistoriesCount(state) {
  return state.inventory.count.count;
}

export function getInventoryStatus(state) {
  return state.inventory.status;
}

export function getProducts(state) {
  return state.item.items.products ? state.item.items.products : [];
}

export function getProductsCount(state) {
  return state.item.items.count;
}

export function getProductsById(state) {
  const productsById = getProducts(state).reduce((obj, prod) => {
    obj[prod.itemId || prod.id] = {
      ...prod,
      skusById: prod.skus.reduce((skuObj, sku) => {
        skuObj[sku.itemId] = sku;
        return skuObj;
      }, {}),
    };
    return obj;
  }, {});
  return productsById;
}

export function getActivateProductsById(state) {
  let products = getProducts(state).filter((prod) => {
    for (let sku of prod.skus) {
      if (sku.isActive === false) {
        return false;
      } else {
        return true;
      }
    }
  });
  return products.reduce((obj, p) => {
    obj[p.id] = p;
    return obj;
  }, {});
}

export function getMarketProductsById(state) {
  const modifiers = getModifiers(state).reduce((obj, m) => {
    obj[m.itemId] = m;
    return obj;
  }, {});
  const categories = getCategories(state).reduce((obj, c) => {
    obj[c.itemId] = c;
    return obj;
  }, {});
  return getProducts(state).reduce((obj, prod) => {
    obj[prod.itemId] = {
      ...prod,
      modifiers: {
        items: getMarketModifiers(
          state.item.items.productModifiers || [],
          prod.itemId,
          modifiers,
        ),
      },
      categories: {
        items: (state.item.items.categoryProducts || [])
          .filter((item) => item.productId === prod.itemId)
          .map((item) => categories[item.categoryId]),
      },
    };
    return obj;
  }, {});
}

export function getModifiers(state) {
  return state.item.items.modifiers ? state.item.items.modifiers : [];
}

export function getCategories(state) {
  return state.item.items.categories ? state.item.items.categories : [];
}

export function getCategoriesById(state) {
  return getCategories(state).reduce((obj, cate) => {
    obj[cate.itemId] = cate;
    return obj;
  }, {});
}

// 비품 (Equipment) — 카테고리 패턴 동일. itemId = id.
export function getEquipments(state) {
  return state.item.items.equipments ? state.item.items.equipments : [];
}

export function getEquipmentsById(state) {
  return getEquipments(state).reduce((obj, eq) => {
    obj[eq.itemId] = eq;
    return obj;
  }, {});
}

// 비품 히스토리 (state.equipment 슬라이스 — reducers/equipment.js)
export function getEquipmentHistories(state) {
  return state.equipment.histories || [];
}

export function getEquipmentHistoriesCount(state) {
  return state.equipment.count || 0;
}

export function getEquipmentHistoriesStatus(state) {
  return state.equipment.status;
}

// 품목코드 체계 (ProductCodeRule) — state.productCode 슬라이스
export function getProductCodeRules(state) {
  return state.productCode.rules || [];
}

export function getProductCodeRulesStatus(state) {
  return state.productCode.status;
}

export function getCategoryProducts(state) {
  return state.item.items.categoryProducts
    ? state.item.items.categoryProducts
    : [];
}

export function getProductModifiers(state) {
  return state.item.items.productModifiers
    ? state.item.items.productModifiers
    : [];
}

export function getModifiersById(state) {
  return getModifiers(state).reduce((obj, mod) => {
    obj[mod.itemId] = mod;
    return obj;
  }, {});
}

export function getLoyaltyReportStatus(state) {
  return state.store.loyaltyReportStatus;
}

export function getCustomersStats(state) {
  return state.customer.customersStats.byId;
}

export function getCustomersStatsCount(state) {
  return state.customer.customersStats.count;
}

export function getCustomerHistoryStatus(state) {
  return state.customer.historyStatus;
}

export function getCurrentLocationId(state) {
  const location = getCurrentLocation(state);
  if (location.id) {
    return location.id;
  }
  return null;
}

export function getCurrentLocationBilling(state) {
  const location = getCurrentLocation(state);
  return location.billing || {};
}

export function isMultiLocation(state) {
  const policy = getStorePolicy(state);
  return policy.multiLocation ? true : false;
}

export function isHeadOffice(state) {
  const isMulti = isMultiLocation(state);
  return isMulti && Authority.level() === "store";
}

// 여러개 지점 혹은 여러 채널을 보유한 것을 확인할때
export function isMultiChannel(state) {
  const policy = getStorePolicy(state);
  return policy?.multiLocation || !_.isEmpty(policy?.provider);
}

export function hasLocationInventory(state) {
  const hasMulti = isMultiLocation(state);
  const locations = getInventoryLocations(state);
  return (!hasMulti && (locations || []).length > 0) || hasMulti;
}

export function getCurrentLocation(state) {
  const organization = getCurrentOrganization(state);
  if (organization.locationId) {
    const locations = getStoreLocationsById(state);
    return locations[organization.locationId] || {};
  }
  return state.store.store.location ? state.store.store.location : {};
}

export function getSalesReportStatus(state) {
  return state.report.salesStatus;
}

export function getSalesReport(state) {
  return state.report.sales;
}

export function getItemsReportStatus(state) {
  return state.report.itemsStatus;
}

export function getItemsReport(state) {
  return state.report.items;
}

export function getOrderReport(state) {
  return state.report.order;
}

export function getCustomerReport(state) {
  return state.report.customer;
}

export function getCustomerVisitReport(state) {
  return state.report.customerVisit;
}

export function getCustomerVisitReportStatus(state) {
  return state.report.customerVisitStatus;
}

export function getCustomerSalesReport(state) {
  return state.report.customerSales;
}

export function getCustomerSalesReportStatus(state) {
  return state.report.customerSalesStatus;
}

export function getCustomerSummary(state) {
  return state.report.customerSummary;
}

export function getCustomerSummaryStatus(state) {
  return state.report.customerSummaryStatus;
}

export function getCustomerDemographicReport(state) {
  return state.report.customerDemographic;
}

export function getCustomerDemographicReportStatus(state) {
  return state.report.customerDemographicStatus;
}

export function getItemsDemographicReport(state) {
  return state.report.itemsDemographic;
}

export function getItemsDemographicReportStatus(state) {
  return state.report.itemsDemographicStatus;
}

export function getOrderReportStatus(state) {
  return state.report.orderStatus;
}

export function getDiscountReport(state) {
  return state.report.discount;
}

export function getDiscountReportStatus(state) {
  return state.report.discountStatus;
}

export function getDiscountTopProductsReport(state) {
  return state.report.discountTopProducts;
}

export function getDiscountTopProductsReportStatus(state) {
  return state.report.discountTopProductsStatus;
}

export function getChannelOrderReport(state) {
  return state.report.channel;
}

export function getChannelOrderReportStatus(state) {
  return state.report.channelStatus;
}

export function getPointReport(state) {
  return state.report.point;
}

export function getPointReportStatus(state) {
  return state.report.pointStatus;
}

// 리포트 v2 게이트④ 전용 — 매장 리포트 봉투 (스위치 off 면 {})
export function getStoreListReport(state) {
  return state.report.storeList;
}

/** v2 제품 리포트 봉투 (스위치 off 면 빈 객체) */
export function getProductReport(state) {
  return state.report.product;
}

/** v2 할인 봉투. v1 getDiscountReport(report.discount) 와 다른 슬롯이다 */
export function getDiscountV2Report(state) {
  return state.report.discountV2;
}

/** v2 고객 봉투. v1 getCustomerReport(report.customer) 와 다른 슬롯이다 */
export function getCustomerV2Report(state) {
  return state.report.customerV2;
}

/** 매장 메모 (Notice type='hq-note') — 조회 전용 */
export function getStoreMemos(state) {
  return state.report.storeMemos || [];
}

/** v2 멤버십 봉투. v1 loadMembershipReport(report.membershipReport) 와 다른 슬롯이다 */
export function getMembershipV2Report(state) {
  return state.report.membershipV2;
}

export function getDeliveryReport(state) {
  return state.report.delivery;
}

export function getDeliveryReportStatus(state) {
  return state.report.deliveryStatus;
}

export function getTrsReport(state) {
  return state.report.trs;
}

export function getTrsItemReport(state) {
  return state.report.trsItem;
}

export function getLoyaltyReport1(state) {
  return state.report.loyalty;
}

export function getLoyaltyReport1Status(state) {
  return state.report.loyaltyStatus;
}

export function getSalesHistoriesStatus(state) {
  return state.report.dailyStatus;
}

export function getSalesHistories(state) {
  return state.report.daily;
}

export function getOrderHistoriesStatus(state) {
  return state.order.historiesStatus;
}

export function getOrderHistoriesStatsStatus(state) {
  return state.order.historiesStatsStatus;
}

export function getSubscriptionReport(state) {
  return state.report.subscription;
}

export function getSubscriptionReportStatus(state) {
  return state.report.subscriptionStatus;
}

export function getInventoryReport(state) {
  return state.report.inventory;
}

export function getInventoryReportStatus(state) {
  return state.report.inventoryStatus;
}

export function getMembershipReportStatus(state) {
  return state.report.membershipStatus;
}

export function loadMembershipReport(state) {
  return state.report.membershipReport;
}

export function getOrderHistories(state) {
  return state.order.histories;
}

export function getPointTransactions(state) {
  return state.store.points;
}

export function getPointTransactionStatus(state) {
  return state.store.pointStatus;
}

export function getOrdersById(state) {
  return state.order.orders.byId;
}

export function getOrdersByReceiptId(state) {
  return Object.values(state.order.orders.byId).reduce((obj, o) => {
    obj[o.receiptId] = o;
    return obj;
  }, {});
}

export function getOrders(state) {
  return state.order.orders;
}

export function getShippingOrders(state) {
  return Object.values(state.order.orders.byId).filter(
    (o) => o.retailOption === "shipping",
  );
}

export function getNotDeliveredOrdersCount(state) {
  return Object.values(state.order.orders.byId).filter(
    (o) =>
      o.diningState &&
      o.diningState !== "delivered" &&
      o.diningState !== "cancelled",
  ).length;
}

export function getOrderStatus(state) {
  return state.order.orderStatus;
}

export function getCustomers(state) {
  return Object.values(state.customer.customers.byId);
}

export function getCustomerCount(state) {
  return state.customer.customers.count;
}

export function getCustomersByTag(state) {
  return Object.values(state.customer.customersByTag.byId);
}

export function getCustomerCountByTag(state) {
  return state.customer.customersByTag.count;
}

export function getCustomersByPhone(state) {
  return getCustomers(state).reduce((obj, c) => {
    obj[c.phone] = c;
    return obj;
  }, {});
}

export function getCustomersById(state) {
  return state.customer.customers.byId;
}

export function getRetrievedCustomer(state) {
  return state.customer.retrieved;
}

export function getCustomerStatus(state) {
  return state.customer.customerStatus;
}

export function getCustomerGroups(state) {
  return state.customer.groups.items || [];
}

export function getCustomerGroupsById(state) {
  return (state.customer.groups.items || []).reduce((obj, g) => {
    obj[g.id] = g;
    return obj;
  }, {});
}

export function getCustomerList(state) {
  return state.customer.groups.list || [];
}

export function getCustomerGroupStatus(state) {
  return state.customer.groupStatus;
}

export function getCustomerTags(state) {
  return Object.values(getCustomerTagsById(state));
}

export function getCustomerTagsById(state) {
  return state.customer.tagsById || {};
}

export function getCustomerTagStatus(state) {
  return state.customer.tagStatus;
}

export function getLoyaltySummary(state) {
  return state.report.loyalty;
}

export function getLoyaltySummaryStatus(state) {
  return state.report.loyaltyStatus;
}

export function getSalesSummary(state) {
  return state.report.summary;
}

export function getSalesSummaryStatus(state) {
  return state.report.summaryStatus;
}

// Employee
export function getEmployee(state) {
  return state.employee.employee || {};
}

export function getEmployeesById(state) {
  return state.employee.employees || {};
}

export function getAllEmployeesById(state) {
  let employee = state.employee.employee || {};
  let employeesById = state.employee.employees || {};
  employeesById = {
    ...employeesById,
    [employee.id]: employee,
  };
  return employeesById;
}

export function getCurrentOrganization(state) {
  return state.employee.organization ?? null;
}

export function getOrganizations(state) {
  return state.employee.organizations || [];
}

export function getOrganizationsByEmployeeId(state) {
  const employeesById = getOrganizations(state).reduce((obj, org) => {
    obj[org.employee.id] = {
      ...org.employee,
      organizations: [
        ...(obj[org.employee.id]?.organizations || []),
        _.cloneDeep(org),
      ],
    };
    return obj;
  }, {});
  return employeesById;
}

export function getOrganizationsByEmployee(state) {
  const employees = Object.values(
    getOrganizations(state).reduce((obj, org) => {
      obj[org.employee.id] = {
        ...org.employee,
        organizations: [
          ...(obj[org.employee.id]?.organizations || []),
          _.cloneDeep(org),
        ],
      };
      return obj;
    }, {}),
  );
  return employees;
}

export function hasLocationAdminAccess(state) {
  // 전체관리자 권한이 있는지
  const currentOrg = getCurrentOrganization(state);
  const isMulti = isMultiLocation(state);
  return (isMulti && !currentOrg.locationId) || !isMulti;
}

/**
 * 브랜드 메시지 진입점을 노출할 계정인가 — 본사 접근 권한이 기준이다(PM 확정).
 * 채널 상태를 보는 canUseBrandMessage 와 축이 다르다: 이건 "이 계정이 애초에
 * 브랜드 메시지를 쓸 수 있는 자리인가"다.
 *
 * 지점장(manager)의 기본 권한 목록에는 kakaochannel 이 없어(services/Authority.js
 * defineRulesFor) 앱마켓에서 카카오채널 연동 자체가 막힌다. 채널이 없으면 신청도
 * 발송도 불가능하므로 진입점을 감춘다.
 */
export function hasBrandMessageAccess(state) {
  return hasLocationAdminAccess(state);
}

export function isIndirectLocationManager(state) {
  const location = getCurrentLocation(state);
  const isMulti = isMultiLocation(state);
  const currentOrg = getCurrentOrganization(state);
  return (
    !isMulti ||
    (isMulti &&
      location.type === "indirect" &&
      currentOrg.locationId === location.id)
  );
}

export function getEmployeeStatus(state) {
  return state.employee.status;
}

export function getEmployeeStatusError(state) {
  return state.employee.status.error;
}

export function isEmployeeStatusFetching(state) {
  return state.employee.status.isFetching;
}

// Around market
export function getProductsByStoreId(state) {
  return state.market.products;
}

export function getSkusById(state) {
  if (state.item.items.products) {
    return _.cloneDeep(state.item.items.products).reduce((obj, prod) => {
      for (let sku of prod.skus) {
        obj[sku.itemId] = {
          ...sku,
          parentId: prod.itemId,
          taxId: prod.taxId,
          trackStock: sku.stockTrack || prod.trackStock,
        };
      }
      return obj;
    }, {});
  }
  return {};
}

export function getSkusByUnit(state) {
  if (state.item.items.products) {
    return _.cloneDeep(state.item.items.products).reduce((obj, prod) => {
      for (let sku of prod.skus) {
        const data = {
          ...sku,
          parentId: prod.itemId,
          taxId: prod.taxId,
          trackStock: sku.stockTrack || prod.trackStock,
        };
        [sku.barcode, sku.unit].filter(Boolean).forEach((code) => {
          obj[code] = data;
        });
      }
      return obj;
    }, {});
  }
  return {};
}

export function getPointStatsByStoreId(state) {
  return state.market.stats;
}

export function getUser(state) {
  return state.market.user;
}

export function getMarketUserPaymentMethod(state) {
  return state.market.user.payments || [];
}

export function getMarketUserSubscription(state) {
  const subscriptions = state.market.user.subscriptions || [];
  return subscriptions.reduce((obj, sub) => {
    obj[sub.id] = sub;
    return obj;
  }, {});
}

export function isUserStatusFetching(state) {
  return state.market.status.isFetching;
}

export function getUserStatusError(state) {
  return state.market.status.error;
}

export function getUserStatusAction(state) {
  return state.market.status.action;
}

export function getMarketStatus(state) {
  return state.market.status;
}

export function getMarketOrderStatus(state) {
  return state.market.orderStatus;
}

export function getMarketStoreStatus(state) {
  return state.market.storeStatus;
}

export function getMarketStores(state) {
  return state.market.stores;
}

export function getMarketStorePolicy(state) {
  return state.market.store.policy;
}

export function getMarketStoreInfo(state) {
  return {
    ...(state.market.store.info || {}),
    ...(state.market.store.location || {}),
  };
}

export function getMarketStoreLocationIds(state) {
  return state.market.store.locationIds || [];
}

export function getMarketStoreConfig(state) {
  return state.market.store.market || {};
}

export function getMarketStoreStats(state) {
  return state.market.store.stats || {};
}

export function getMarketStoreLoyalty(state) {
  return state.market.store.loyalty || {};
}

export function getMarketStoreTaxes(state) {
  return (state.market.store.taxes || []).reduce((obj, tax) => {
    obj[tax.id] = tax;
    return obj;
  }, {});
}

export function getMarketStoreId(state) {
  return state.market.store.id;
}

export function getMarketStorePoint(state) {
  return _.sum(
    (state.market.store.points?.items || []).map((p) =>
      p.type === "save" ? p.point : -p.point,
    ),
  );
}

export function getMarketStoreCredit(state) {
  return _.sum(
    (state.market.store.credits?.items || []).map((c) =>
      c.type === "deposit" ? c.amount : -c.amount,
    ),
  );
}

export function getMarketReceiptStore(state) {
  return state.market.receiptStore || {};
}

export function getCart(state) {
  return state.market.cart || {};
}

export function getCartStoreInfo(state) {
  return {
    ...(state.market.cart.store?.info || {}),
    ...(state.market.cart.store?.location || {}),
    policy: state.market.cart.store?.policy || {},
  };
}

export function getCartStoreMarketConfig(state) {
  return state.market.cart.store?.market || {};
}

export function getSkuProducts(state) {
  if (state.item.items.products) {
    return _.cloneDeep(state.item.items.products).reduce((obj, prod) => {
      for (let sku of prod.skus) {
        obj[sku.itemId] = prod;
      }
      return obj;
    }, {});
  }
  return {};
}

export function getCartSavePoint(state) {
  const point = fromMarket.getLoyaltySavePointByOrder(
    getCart(state),
    getMarketStoreLoyalty(state),
    getSkuProducts(state),
  );
  return point;
}

export function getCartUsePoint(state) {
  const point = fromMarket.getLoyaltyUsePointByOrder(
    getCart(state),
    getMarketStoreLoyalty(state),
  );
  return point;
}

export function isCartSavablePoint(state) {
  const point = getCartSavePoint(state);
  return !_.isEmpty(point) && point.point > 0;
  // TODO
  // if (customer) {
  //   const transactions = getPointsByCustomerId(state);
  //   return fromStore.isSavablePoint(
  //     getStoreLoyalty(state),
  //     customer,
  //     getCheckout(state),
  //     getCheckoutSavePoint(state),
  //     transactions[customer.id],
  //   );
  // }
  // return false;
}

export function isSidebarOpen(state) {
  return state.market.sidebar;
}

export function isMarketAuthenticated(state) {
  return state.market.isAuthenticated;
}

export function getCheckout(state) {
  return state.market.checkout;
}

// Coupon

export function getCouponsById(state) {
  return state.coupon.byId || {};
}

export function getBarcodeCouponsById(state) {
  const _coupons = getCouponsById(state);
  return Object.values(_coupons)
    .filter(
      (coupon) => coupon.type === "general" && coupon.method === "barcode",
    )
    .reduce((obj, c) => {
      obj[c.id] = c;
      return obj;
    }, {});
}

export function getCouponStatus(state) {
  return state.coupon.status;
}

export function getCouponTransactionSearchResult(state) {
  return state.coupon.transactionSearchResult || { items: [], count: 0 };
}

export function getCouponIssueSuccessCount(state) {
  return state.coupon.issueSuccessCount;
}

export function getSearchedCouponTransactions(state) {
  console.log(state.coupon.searchedTransactions.byId);
  return state.coupon.searchedTransactions.byId || {};
}

export function hasMoreCouponTransactions(state) {
  return state.coupon.searchedTransactions.nextToken || null;
}

export function getCouponAction(state) {
  return state.coupon.status.action;
}

export function getProviderCouponsById(state) {
  return state.coupon.providerById || {};
}

export function getPublishableCouponsById(state) {
  const barcodeCouponsById = getBarcodeCouponsById(state);
  const providerCouponsById = getProviderCouponsById(state);
  return { ...barcodeCouponsById, ...providerCouponsById };
}

// Subscription
export function isSubscriptionFetching(state) {
  return state.subscription.status.isFetching;
}

export function getSubscriptionError(state) {
  return state.subscription.status.error;
}

export function getSubscriptionAction(state) {
  return state.subscription.status.action;
}

export function getSubscriptionPlans(state) {
  return state.subscription.plans || {};
}

export function getEditingSubscriptionPlan(state) {
  return state.subscription.editing || {};
}

export function getSubscriptionEvents(state) {
  return state.subscription.events || [];
}

export function getSubscriptionEditing(state) {
  return state.subscription.subscriptionEditing || {};
}

// Settlements
export function getSettlements(state) {
  return state.settlements.settlements;
}

export function getSettlementsStatus(state) {
  return state.settlements.status;
}

// Terminals

export function getTerminals(state) {
  return state.terminals.terminals;
}

export function getTerminalStatus(state) {
  return state.terminals.status;
}

// PurchaseOrders

export function getPurchaseOrdersById(state) {
  return state.purchaseOrder.byId;
}

export function getPurchaseOrders(state) {
  return Object.values(getPurchaseOrdersById(state));
}

export function getPurchaseOrderStatus(state) {
  return state.purchaseOrder.status;
}

export function getPurchaseOrderCount(state) {
  return state.purchaseOrder.count;
}

// ===== 배송주문 관리 — 발주(주문 가져오기/발주확인/매칭/합포) =====

export function getDeliveryStatus(state) {
  return state.delivery.status;
}

export function getDeliveryImportStatus(state) {
  return state.delivery.importStatus;
}

export function getDeliveryConfirmStatus(state) {
  return state.delivery.confirmStatus;
}

export function getDeliveryMatchStatus(state) {
  return state.delivery.matchStatus;
}

export function getDeliveryMergeStatus(state) {
  return state.delivery.mergeStatus;
}

export function getManualOrderStatus(state) {
  return state.delivery.manualOrderStatus;
}

export function getPreConfirmOrders(state) {
  return state.delivery.preConfirmOrders;
}

export function getDeliveryStageOrders(state) {
  return state.delivery.stageOrders;
}

export function getDeliveryConfirmBatches(state) {
  return state.delivery.confirmBatches;
}

export function getDeliveryItemLinks(state) {
  return state.delivery.itemLinks;
}

export function getDeliveryImportResult(state) {
  return state.delivery.importResult;
}

export function getDeliveryLastSyncedAt(state) {
  return state.delivery.lastSyncedAt;
}

export function getDeliveryReloadResult(state) {
  return state.delivery.reloadResult;
}

export function getDeliveryConfirmResult(state) {
  return state.delivery.confirmResult;
}

export function getDeliveryAutoMatchResult(state) {
  return state.delivery.autoMatchResult;
}

export function getDeliveryMergeCheckResult(state) {
  return state.delivery.mergeCheckResult;
}

export function getDeliveryMergeResult(state) {
  return state.delivery.mergeResult;
}

export function getDeliveryCancelResult(state) {
  return state.delivery.cancelResult;
}

export function getManualOrderResult(state) {
  return state.delivery.manualOrderResult;
}

export function getUploadManualOrderRowsResult(state) {
  return state.delivery.uploadRowsResult;
}

// ===== 배송주문 관리 — 송장(발번/출력차수/발송처리/가송장/배송관리) =====

export function getShipmentStatus(state) {
  return state.shipment.status;
}

export function getShipmentIssueStatus(state) {
  return state.shipment.issueStatus;
}

export function getShipmentPushStatus(state) {
  return state.shipment.pushStatus;
}

export function getShipmentProvisionStatus(state) {
  return state.shipment.provisionStatus;
}

export function getShipmentBatchStatus(state) {
  return state.shipment.batchStatus;
}

export function getPrintTargets(state) {
  return state.shipment.printTargets;
}

// 보류 목록(활성 DELIVERY_HOLD — holdReasonCodes 부착, 별도 분리 노출)
export function getPrintHolds(state) {
  return state.shipment.printHolds;
}

// 오류 목록(unpaid|receiver|address) — 화면 구분 노출은 추후 범위, 데이터만
export function getPrintErrors(state) {
  return state.shipment.printErrors;
}

export function getShippingStageTab(state) {
  return state.shipment.stageTab;
}

export function getPrintBatches(state) {
  return state.shipment.printBatches;
}

export function getProvisionOrders(state) {
  return state.shipment.provisionOrders;
}

export function getIssueTrackingResult(state) {
  return state.shipment.issueResult;
}

export function getPushShipmentResult(state) {
  return state.shipment.pushResult;
}

export function getApplyExternalTrackingResult(state) {
  return state.shipment.applyExternalResult;
}

export function getProvisionResult(state) {
  return state.shipment.provisionResult;
}

export function getShipmentCommandResult(state) {
  return state.shipment.commandResult;
}

export function getPrintBatchResult(state) {
  return state.shipment.printBatchResult;
}

export function getExportPrintTargetsUrl(state) {
  return state.shipment.exportUrl;
}

export function getTrackShipmentResult(state) {
  return state.shipment.trackResult;
}

export function getDeliveryDelays(state) {
  return state.shipment.delays;
}

// ----- 배송 환경설정 (deliverySettings 도메인) -----

export function getShippingConfig(state) {
  return state.deliverySettings.config;
}

export function getShippingCarriers(state) {
  return state.deliverySettings.carriers;
}

export function getShippingSettingsStatus(state) {
  return state.deliverySettings.status;
}

export function getShippingSettingsSaveStatus(state) {
  return state.deliverySettings.saveStatus;
}

export function getSkuMergeRules(state) {
  return state.deliverySettings.mergeRules;
}

export function getSkuMergeRulesStatus(state) {
  return state.deliverySettings.mergeRulesStatus;
}

export function getSkuMergeUpdateResult(state) {
  return state.deliverySettings.mergeUpdateResult;
}

export function getUnusedTrackings(state) {
  return state.deliverySettings.unusedTrackings;
}

export function getUnusedTrackingsStatus(state) {
  return state.deliverySettings.unusedStatus;
}

// 기본 택배사 코드 — ShippingConfig.carrierId → Carrier.code (발번·회수 폼 초기값용,
// 미로드/미지정 시 "cj" 폴백)
export function getDefaultCarrierCode(state) {
  const config = state.deliverySettings.config;
  const carriers = state.deliverySettings.carriers || [];
  const found = config?.carrierId
    ? carriers.find((carrier) => carrier?.id === config.carrierId)
    : null;
  return found?.code || "cj";
}

// ===== 배송주문 관리 — CS(클레임/문의/수집·승인/결제·정산) =====

export function getCsStatus(state) {
  return state.cs.status;
}

export function getCsClaimStatus(state) {
  return state.cs.claimStatus;
}

export function getCsEditStatus(state) {
  return state.cs.editStatus;
}

export function getCsCollectStatus(state) {
  return state.cs.collectStatus;
}

export function getCsPaymentStatus(state) {
  return state.cs.paymentStatus;
}

export function getCsTasks(state) {
  return state.cs.tasks;
}

export function getCsCounts(state) {
  return state.cs.counts;
}

export function getCsActivitiesByOrder(state) {
  return state.cs.activitiesByOrder;
}

export function getCsSearchResult(state) {
  return state.cs.searchResult;
}

export function getCsActionResult(state) {
  return state.cs.actionResult;
}

// Brands — store.js에 brandsById sub-reducer로 마운트됨. 로딩/에러는 storeStatus 공용 사용.

export function getBrandsById(state) {
  return state.store.brandsById || {};
}

export function getBrands(state) {
  return Object.values(getBrandsById(state) || {});
}

export function getBrandByCode(state, code) {
  if (!code) return null;
  return (
    Object.values(getBrandsById(state) || {}).find((b) => b.code === code) ||
    null
  );
}

// Suppliers

export function getSuppliersById(state) {
  return state.supplier.byId || {};
}

export function getSuppliersName(state) {
  const suppliers = Object.values(getSuppliersById(state));
  return suppliers.map((sup) => sup.name.trim());
}

export function getSupplierStatus(state) {
  return state.supplier.status;
}

// MGMT

export function getMgmtStatus(state) {
  return state.mgmt.status;
}

export function getMgmtContract(state) {
  return state.mgmt.contract;
}

export function getSearchedMgmtStores(state) {
  return state.mgmt.searched;
}

export function getMgmtLocations(state) {
  return state.mgmt.locations;
}

export function getSearchedOperatorStores(state) {
  return state.mgmt.searchedStores;
}

export function getCurrentLocationMenuConfig(state) {
  return getCurrentLocation(state)?.menu || {};
}

export function getMenuBoardConfig(state) {
  let config = getCurrentLocationMenuConfig(state);
  return { ...(config.board || {}), pages: config.board?.pages || [] };
}

// providerConfig

export function getStoreProviderConfig(state) {
  return state.providerConfig.config || {};
}

// fallback 은 아래 EMPTY_SHOPS 와 같은 이유로 모듈 상수다 — 매번 새 [] 를 만들면
// useSelector 가 항상 참조 불일치로 보고 구독 컴포넌트가 모든 액션마다 리렌더된다.
const EMPTY_COUPON_IDS = [];

export function getOfflineUsableCouponIds(state) {
  return state.providerConfig.config?.usableCouponIds || EMPTY_COUPON_IDS;
}

export function getProviderConfigStatus(state) {
  return state.providerConfig.status || {};
}

export function getStorePolicyProvider(state) {
  return state.store.store.policy?.provider || {};
}

// fallback 은 모듈 상수로 고정한다. 셀렉터가 매번 새 []/{} 를 만들면 useSelector 가
// 항상 참조 불일치로 보고 리렌더 → 로컬 state 동기화 useEffect 가 무한 루프에 빠진다.
const EMPTY_SHOPS = [];
const EMPTY_SHOPS_STATUS = {};

// 자사몰이 내려준 서브몰 후보 목록 (선택 모달용 임시 데이터).
// 활성 서브몰의 영속 소스는 getStorePolicyProvider(state).shopMap 이다 — 혼동 주의.
export function getAvailableShops(state) {
  return state.providerConfig.availableShops || EMPTY_SHOPS;
}

export function getAvailableShopsStatus(state) {
  return state.providerConfig.availableShopsStatus || EMPTY_SHOPS_STATUS;
}

export function getStoreProviderShopMap(state) {
  return getStorePolicyProvider(state).shopMap || EMPTY_SHOPS;
}

export function getCafe24Provider(state) {
  const provider = getStorePolicyProvider(state);
  return provider?.key === "cafe24" ? provider : {};
}

export function getImwebProvider(state) {
  const provider = getStorePolicyProvider(state);
  return provider?.key === "imweb" ? provider : {};
}

export function isCafe24Provider(state) {
  const provider = getStorePolicyProvider(state);
  return provider?.key === "cafe24";
}

export function isImwebProvider(state) {
  const provider = getStorePolicyProvider(state);
  return provider?.key === "imweb";
}

export function isOutsourcedCustomer(state) {
  const provider = getStorePolicyProvider(state);
  return !_.isEmpty(provider) && provider.customer;
}

export function hasConnectCafe24Inventory(state) {
  const cafe24Provider = getCafe24Provider(state);
  return (
    !_.isEmpty(cafe24Provider) &&
    cafe24Provider.inventoryLocationId &&
    cafe24Provider.inventory
  );
}

export function getCafe24SharedLocationId(state) {
  const cafe24Provider = getCafe24Provider(state);
  return !_.isEmpty(cafe24Provider) ? cafe24Provider.inventoryLocationId : null;
}

export function getUpdateTierHistories(state) {
  return state.providerConfig.histories || [];
}

export function getStockSyncHistories(state) {
  return state.providerConfig.inventoryHistories || [];
}

export function getLinkHistories(state) {
  return state.providerConfig.linkHistories || [];
}

// waiting

export function getWaiting(state) {
  return state.waiting.waiting;
}

export function getWaitingStatus(state) {
  return state.waiting.status;
}

export function getWaitingInfo(state) {
  return state.waiting.info;
}

export function getCreditConfig(state) {
  const _isMultiLocation = isMultiLocation(state);
  const location = getCurrentLocation(state);
  return _isMultiLocation && Authority.level() !== "store"
    ? location.credit || {}
    : state.store.store?.credit || {};
}

export function getStoreCreditStatus(state) {
  return state.store.credit || 0;
}

export function getCreditTransactionHistoriesById(state) {
  return state.store.creditHistories.byId;
}

export function getCreditTransactionHistoriesCount(state) {
  return state.store.creditHistories.count;
}

export function getProviderOrdersById(state) {
  return state.order.providerOrders.items;
}

export function getProviderOrdersCount(state) {
  return state.order.providerOrders.count;
}

export function getExternalCustomerGroups(state) {
  return state.providerConfig.groups || [];
}

export function getProductsFromProvider(state) {
  return state.providerConfig.providerProducts;
}

export function getDraftOnoffConfig(state) {
  return state.providerConfig.onoffDraftConfig;
}

export function getLiveOnoffConfig(state) {
  return state.providerConfig.onoffLiveConfig;
}

export function getSearchedCherryPickers(state) {
  return state.providerConfig.cherryPickers;
}

export function getSearchedBlackList(state) {
  return Object.values(state.providerConfig.blackList || {});
}

export function getSearchedBlackListById(state) {
  return state.providerConfig.blackList;
}

export function getSearchedCustomerTierHistories(state) {
  return state.providerConfig.customerTierHistories;
}

export function getMirrorConfig(state) {
  const hasMulti = isMultiLocation(state);
  const location = getCurrentLocation(state);
  const store = getStore(state);
  if (hasMulti && Authority.level() !== "store") {
    return location.mirror || {};
  } else {
    return store.mirror || {};
  }
}

export function getDualConfig(state) {
  return state.store.store.mirror || {};
}

export function getSenderName(state) {
  const storeInfo = getStoreInfo(state);
  const isMulti = isMultiLocation(state);
  const org = getCurrentOrganization(state);
  const location = getCurrentLocation(state);
  if (isMulti) {
    if (["owner", "admin"].includes(org.role)) {
      return storeInfo.name;
    } else {
      return location.name;
    }
  } else {
    return storeInfo.name;
  }
}

export function getSenderPhone(state) {
  const storeInfo = getStoreInfo(state);
  const isMulti = isMultiLocation(state);
  const org = getCurrentOrganization(state);
  const location = getCurrentLocation(state);
  if (isMulti) {
    if (["owner", "admin"].includes(org.role)) {
      return storeInfo.phone;
    } else {
      return location.phone;
    }
  } else {
    return storeInfo.phone;
  }
}

export function getSearchedProductInventories(state) {
  return state.inventory.searched.list || [];
}

export function getSearchedProductInventoryCount(state) {
  return state.inventory.searched.count;
}

export function getSearchedSkuInventoryReport(state) {
  return state.inventory.skuReport;
}

export function getSearchedProductInventoriesForExport(state) {
  return state.inventory.searchedForExport.list || [];
}

export function getNeedPayModal(state) {
  return state.local.needPayModal;
}

export function getNotContractModal(state) {
  return state.local.notContractModal;
}

export function getContractStatus(state) {
  return state.store.contractStatus;
}

export function getAllContractPlans(state) {
  return state.store.contractPlans || [];
}

export function getCurrentLocationStats(state) {
  return state.store.locationStats;
}

export function isOver100Store(state) {
  const stats = getCurrentLocationStats(state);
  const hasContract = isContracted(state);
  return !hasContract && stats?.sales?.thisMonthCount > 100;
}

export function loadCustomerEventsById(state) {
  return state.campaign.customerEventsById;
}

export function getCustomerEventStatus(state) {
  return state.campaign.customerEventStatus;
}

export function loadCustomerEventReport(state) {
  return state.campaign.customerEventReport;
}

export function loadCustomerEventUserList(state) {
  return state.campaign.customerEventUserList.customer;
}

export function loadCustomerEventInstagramList(state) {
  return state.campaign.customerEventUserList.instagram;
}

export function getCampaignTemplatesById(state) {
  return state.campaign.campaignTemplates;
}
